// src/pages/ChatPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Empty, Button, message } from 'antd';
import { MessageOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useAuth } from '../App';
import { chatAPI, doctorLinkAPI, authAPI } from '../utils/api';
import { ChatComponent } from '../components/ChatComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const { Title, Text } = Typography;

interface Conversation {
  id: number;
  patient_id: number;
  doctor_id: number;
  is_support?: boolean;
  created_at: string;
  updated_at: string;
  unread_count?: number;
  patient_full_name?: string | null;
  patient?: {
    id: number;
    full_name: string;
    email: string;
  };
  last_message?: {
    id: number;
    encrypted_content: string;
    created_at: string;
  };
}

type DoctorLinkStatus = 'none' | 'pending' | 'active' | 'loading';

export const ChatPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [supportConversation, setSupportConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [doctorLinkStatus, setDoctorLinkStatus] = useState<DoctorLinkStatus>('loading');
  const [activeDoctorId, setActiveDoctorId] = useState<number | null>(null);

  const refreshSessionUser = useCallback(async () => {
    try {
      const profileResponse = await authAPI.getProfile();
      const profileData = profileResponse.data as any;
      if (profileData) {
        login({
          user: profileData,
          access_token: localStorage.getItem('access_token') || '',
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [login]);

  const loadDoctorLinkStatus = useCallback(async () => {
    if (user?.role !== 'patient') {
      setDoctorLinkStatus('none');
      setActiveDoctorId(null);
      return;
    }
    try {
      setDoctorLinkStatus('loading');
      const response = await doctorLinkAPI.getDoctorLinkState();
      const bundle = (response.data as any)?.bundle;
      const status = bundle?.link?.status;
      if (status === 'pending') {
        setDoctorLinkStatus('pending');
        setActiveDoctorId(null);
      } else if (status === 'active') {
        setDoctorLinkStatus('active');
        const doctorId = bundle?.link?.doctor_id ?? user?.doctor_id ?? null;
        setActiveDoctorId(doctorId);
        // Keep session user fresh after accept (doctor_id may have been stale)
        if (!user?.doctor_id && doctorId) {
          await refreshSessionUser();
        }
      } else {
        setDoctorLinkStatus('none');
        setActiveDoctorId(null);
      }
    } catch (error) {
      console.error('Error loading doctor link state:', error);
      if (user?.doctor_id) {
        setDoctorLinkStatus('active');
        setActiveDoctorId(user.doctor_id);
      } else {
        setDoctorLinkStatus('none');
        setActiveDoctorId(null);
      }
    }
  }, [user?.role, user?.doctor_id, refreshSessionUser]);

  const loadAdminConversation = useCallback(async (convId: number) => {
    if (!Number.isFinite(convId) || convId <= 0) {
      message.error('Некорректный идентификатор чата');
      navigate('/admin/chats', { replace: true });
      return;
    }
    try {
      setLoading(true);
      setSelectedConversation(null);
      const response = await chatAPI.getConversations();
      const convs = (response.data || []) as Conversation[];
      const conversation = convs.find((c) => Number(c.id) === convId && c.is_support);
      if (conversation) {
        setSelectedConversation(conversation);
        return;
      }
      message.warning('Чат не найден или недоступен');
      navigate('/admin/chats', { replace: true });
    } catch (error) {
      console.error('Error loading admin conversation:', error);
      message.error('Не удалось загрузить беседу поддержки');
      navigate('/admin/chats', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  const loadSupportConversation = useCallback(async () => {
    try {
      const response = await chatAPI.getConversations();
      const convs = (response.data || []) as Conversation[];
      const support = convs.find(c => c.is_support);
      if (support) {
        setSupportConversation(support);
      }
    } catch (error) {
      console.error('Error loading support conversation:', error);
      message.error('Не удалось загрузить беседу с поддержкой');
    }
  }, []);
  
  const handleCreateSupport = async () => {
    try {
      const response = await chatAPI.createSupportConversation();
      const newConv = response.data as Conversation;
      setSupportConversation(newConv);
      setShowSupport(true);
      navigate('/chat/support', { replace: true });
      message.success('Беседа с поддержкой создана');
    } catch (error: any) {
      console.error('Error creating support conversation:', error);
      message.error(error.response?.data?.detail || 'Ошибка создания беседы с поддержкой');
    }
  };

  const loadPatientConversation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations();
      const convs = (response.data || []) as Conversation[];
      
      // Filter out support conversations - patient should only see regular chat with doctor
      const regularConversations = convs.filter(c => !c.is_support);
      
      if (regularConversations.length > 0) {
        // Patient should have only one conversation with their doctor (not support)
        const conversation = regularConversations[0];
        setSelectedConversation(conversation);
      } else {
        setSelectedConversation(null);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      message.error('Не удалось загрузить беседу');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDoctorConversation = useCallback(async (convId: number) => {
    if (!Number.isFinite(convId) || convId <= 0) {
      message.error('Некорректный идентификатор чата');
      navigate('/chats', { replace: true });
      return;
    }
    try {
      setLoading(true);
      setSelectedConversation(null);
      const response = await chatAPI.getConversations();
      const convs = (response.data || []) as Conversation[];
      const conversation = convs.find((c) => Number(c.id) === convId);

      if (conversation) {
        setSelectedConversation(conversation);
        return;
      }

      message.warning('Чат не найден или недоступен');
      navigate('/chats', { replace: true });
    } catch (error) {
      console.error('Error loading conversation:', error);
      message.error('Не удалось загрузить беседу');
      navigate('/chats', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user?.role === 'patient') {
      loadDoctorLinkStatus();
      loadPatientConversation();
      loadSupportConversation();
    } else if (user?.role === 'doctor') {
      if (conversationId) {
        if (conversationId === 'support') {
          setShowSupport(true);
          setLoading(false);
          loadSupportConversation();
        } else {
          setShowSupport(false);
          // Keep loading=true until fetch finishes — prevents bounce-back to /chats
          setLoading(true);
          setSelectedConversation(null);
          void loadDoctorConversation(parseInt(conversationId, 10));
          loadSupportConversation();
        }
      } else {
        navigate('/chats', { replace: true });
      }
    } else if (user?.role === 'admin') {
      if (conversationId) {
        setLoading(true);
        setSelectedConversation(null);
        void loadAdminConversation(parseInt(conversationId, 10));
      }
    }
  }, [user, conversationId, navigate, loadPatientConversation, loadSupportConversation, loadDoctorConversation, loadAdminConversation, loadDoctorLinkStatus]);

  useEffect(() => {
    if (user?.role === 'doctor' && !loading && !conversationId && !showSupport) {
      navigate('/chats', { replace: true });
    }
  }, [user?.role, loading, conversationId, showSupport, navigate]);

  // NOTE: do NOT bounce doctor/admin away while selectedConversation is null on first paint.

  const renderSupportEntryButton = () => (
    <Button
      icon={<CustomerServiceOutlined />}
      onClick={() => navigate('/chat/support')}
      style={{ marginTop: 16, width: isMobile ? '100%' : 'auto' }}
      block={isMobile}
    >
      Написать в поддержку
    </Button>
  );

  // For patients - show chat directly with doctor
  if (user?.role === 'patient') {
    if (loading || doctorLinkStatus === 'loading') {
      return (
        <div>
          <Title level={2} style={{ color: '#263238' }}>Чат с врачом</Title>
          <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Загрузка...</Text>
          </Card>
        </div>
      );
    }

    // Show support chat if requested — always available without doctor
    if (showSupport || conversationId === 'support') {
      if (supportConversation) {
        return (
          <div>
            <div style={{ marginBottom: isMobile ? 16 : 24 }}>
              <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Чат с поддержкой</Title>
              <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px', display: 'block', marginTop: 8 }}>Задайте вопрос службе поддержки</Text>
              <Button 
                onClick={() => { setShowSupport(false); navigate('/chat'); }}
                style={{ marginTop: 16, width: isMobile ? '100%' : 'auto' }}
                block={isMobile}
              >
                Вернуться к чату с врачом
              </Button>
            </div>
            <Card style={{ borderRadius: '12px' }}>
              <ChatComponent
                conversationId={supportConversation.id}
                patientId={supportConversation.patient_id}
                doctorId={supportConversation.doctor_id}
                isSupport={true}
              />
            </Card>
          </div>
        );
      } else {
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Title level={2} style={{ color: '#263238' }}>Чат с поддержкой</Title>
              <Text type="secondary">Задайте вопрос службе поддержки</Text>
            </div>
            <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
              <Button 
                type="primary" 
                icon={<CustomerServiceOutlined />}
                size="large"
                onClick={handleCreateSupport}
                style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
              >
                Начать беседу с поддержкой
              </Button>
            </Card>
          </div>
        );
      }
    }

    const hasActiveDoctor = doctorLinkStatus === 'active' && !!(activeDoctorId || user?.doctor_id);
    
    if (!selectedConversation && hasActiveDoctor) {
      // No conversation yet, but patient has an active doctor
      // Create conversation on first message
      return (
        <div>
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Чат с врачом</Title>
            <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px', display: 'block', marginTop: 8 }}>
              Общение с вашим лечащим врачом
            </Text>
            {renderSupportEntryButton()}
          </div>
          <Card style={{ borderRadius: '12px' }}>
            <ChatComponent
              conversationId={0} // Will be created on first message
              patientId={user.id}
              doctorId={activeDoctorId || user.doctor_id!}
              isSupport={false}
              onConversationCreated={(newConversationId) => {
                // Update URL without full page reload
                navigate(`/chat/${newConversationId}`, { replace: true });
              }}
            />
          </Card>
        </div>
      );
    }

    if (selectedConversation && hasActiveDoctor) {
      return (
        <div>
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Чат с врачом</Title>
            <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px', display: 'block', marginTop: 8 }}>
              Общение с вашим лечащим врачом
            </Text>
            {renderSupportEntryButton()}
          </div>
          <Card style={{ borderRadius: '12px' }}>
            <ChatComponent
              conversationId={selectedConversation.id}
              patientId={selectedConversation.patient_id}
              doctorId={selectedConversation.doctor_id}
              isSupport={false}
            />
          </Card>
        </div>
      );
    }

    if (doctorLinkStatus === 'pending') {
      return (
        <div>
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Чат с врачом</Title>
            {renderSupportEntryButton()}
          </div>
          <Card style={{ borderRadius: '12px' }}>
            <Empty
              description={
                <span>
                  Заявка на рассмотрении.
                  <br />
                  Чат с врачом недоступен, пока врач не подтвердит привязку.
                </span>
              }
              image={<MessageOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            />
          </Card>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Чат с врачом</Title>
          {renderSupportEntryButton()}
        </div>
        <Card style={{ borderRadius: '12px' }}>
          <Empty
            description="Врач не назначен"
            image={<MessageOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          />
        </Card>
      </div>
    );
  }

  // For doctors - show specific conversation or support
  if (user?.role === 'doctor') {
    if (showSupport || conversationId === 'support') {
      if (supportConversation) {
        return (
          <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={2} style={{ color: '#263238' }}>Чат с поддержкой</Title>
                <Text type="secondary">Задайте вопрос службе поддержки</Text>
              </div>
              <Button onClick={() => { setShowSupport(false); navigate('/chats'); }}>
                Вернуться к чатам
              </Button>
            </div>
            <Card style={{ borderRadius: '12px' }}>
              <ChatComponent
                conversationId={supportConversation.id}
                patientId={supportConversation.patient_id}
                doctorId={supportConversation.doctor_id}
                isSupport={true}
              />
            </Card>
          </div>
        );
      } else {
        return (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Title level={2} style={{ color: '#263238' }}>Чат с поддержкой</Title>
              <Text type="secondary">Задайте вопрос службе поддержки</Text>
            </div>
            <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
              <Button 
                type="primary" 
                icon={<CustomerServiceOutlined />}
                size="large"
                onClick={handleCreateSupport}
                style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
              >
                Начать беседу с поддержкой
              </Button>
            </Card>
          </div>
        );
      }
    }
    
    if (conversationId && conversationId !== 'support') {
      if (loading || !selectedConversation) {
        return (
          <div>
            <Title level={2} style={{ color: '#263238' }}>Чат с пациентом</Title>
            <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">Загрузка...</Text>
            </Card>
          </div>
        );
      }

      return (
          <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <Title level={2} style={{ color: '#263238', marginBottom: 4 }}>Чат с пациентом</Title>
                <Text type="secondary">
                  Общение с пациентом
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button onClick={() => navigate('/chats')}>
                  К списку чатов
                </Button>
                <Button
                  icon={<CustomerServiceOutlined />}
                  onClick={() => navigate('/chat/support')}
                >
                  Написать в поддержку
                </Button>
              </div>
            </div>
            <Card style={{ borderRadius: '12px' }}>
            <ChatComponent
              conversationId={selectedConversation.id}
              patientId={selectedConversation.patient_id}
              doctorId={selectedConversation.doctor_id}
              isSupport={selectedConversation.is_support}
            />
            </Card>
          </div>
        );
    }
    
    // Redirect handled in useEffect
    return null;
  }
  
  // For admins - show support conversation
  if (user?.role === 'admin') {
    if (loading || (conversationId && !selectedConversation)) {
      return (
        <div>
          <Title level={2} style={{ color: '#263238' }}>Чат поддержки</Title>
          <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Загрузка...</Text>
          </Card>
        </div>
      );
    }
    
    if (selectedConversation) {
      const patientName =
        (selectedConversation as any).patient_full_name ||
        selectedConversation.patient?.full_name ||
        `Пользователь #${selectedConversation.patient_id}`;
      return (
        <div>
          <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2} style={{ color: '#263238' }}>Чат поддержки</Title>
              <Text type="secondary">
                Беседа с {patientName}
              </Text>
            </div>
            <Button onClick={() => navigate('/admin/chats')}>
              Вернуться к списку чатов
            </Button>
          </div>
          <Card style={{ borderRadius: '12px' }}>
            <ChatComponent
              conversationId={selectedConversation.id}
              patientId={selectedConversation.patient_id}
              doctorId={selectedConversation.doctor_id}
              isSupport={selectedConversation.is_support}
            />
          </Card>
        </div>
      );
    }
    
    return null;
  }

  return null;
};
